import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { BRAND_COLORS } from '../styles/theme';
import { useTranslation, LanguageSwitcher } from '../i18n';
import XERLogo from '../components/XERLogo';
import {
  useValidateInvitationQuery,
  useAcceptInvitationMutation,
} from './acceptInvitation.queries';

export const AcceptInvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    fullName: '',
    password: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  // --- Read: validate invitation token ---
  const validationQuery = useValidateInvitationQuery(token);
  const invitationData = validationQuery.data;
  const invitationValid = invitationData?.valid === true;

  // Derive a user-visible error message from the query state
  const validationErrorMsg = validationQuery.isError
    ? t('errors.generic')
    : invitationData && !invitationData.valid
      ? invitationData.error || t('errors.generic')
      : null;

  // --- Mutation: accept invitation ---
  const acceptMutation = useAcceptInvitationMutation();
  const submitting = acceptMutation.isPending;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formData.fullName.trim()) {
      errors.push(t('auth.fullNameRequired'));
    }

    if (!formData.password) {
      errors.push(t('auth.passwordRequired'));
    } else if (formData.password.length < 6) {
      errors.push(t('auth.passwordMinLength'));
    }

    if (formData.password !== formData.confirmPassword) {
      errors.push(t('auth.passwordsDoNotMatch'));
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleAcceptInvitation = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;
    if (!token) {
      setFormErrors([t('invitation.invalidData')]);
      return;
    }

    acceptMutation.mutate(
      { token, password: formData.password, fullName: formData.fullName },
      {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => {
            navigate('/');
          }, 3000);
        },
        onError: (err: unknown) => {
          let errorMessage = t('errors.generic');
          if (err && typeof err === 'object') {
            if (
              'response' in err &&
              err.response &&
              typeof err.response === 'object' &&
              'data' in err.response
            ) {
              const data = err.response.data as { detail?: string };
              if (data.detail) {
                errorMessage = data.detail;
              }
            } else if ('message' in err && typeof err.message === 'string') {
              errorMessage = err.message;
            }
          }
          setFormErrors([errorMessage]);
        },
      }
    );
  };

  if (validationQuery.isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url(/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* Language Switcher - top right */}
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <LanguageSwitcher variant="compact" />
        </Box>

        <Paper
          elevation={24}
          sx={{
            p: 4,
            maxWidth: 500,
            width: '100%',
            mx: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: BRAND_COLORS.forestGreen.main }} />
          <Typography variant="h6">{t('invitation.validating')}</Typography>
        </Paper>
      </Box>
    );
  }

  if (!token || validationErrorMsg || !invitationValid) {
    const displayError = !token
      ? t('invitation.noToken')
      : validationErrorMsg ?? t('errors.generic');

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url(/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* Language Switcher - top right */}
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <LanguageSwitcher variant="compact" />
        </Box>

        <Paper
          elevation={24}
          sx={{
            p: 4,
            maxWidth: 500,
            width: '100%',
            mx: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <XERLogo width={120} />
            <Typography
              variant="h5"
              sx={{ mt: 2, fontWeight: 600, color: BRAND_COLORS.forestGreen.main }}
            >
              {t('invitation.invalidTitle')}
            </Typography>
          </Box>

          <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
            {displayError}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('invitation.invalidMessage')}
          </Typography>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{
              borderColor: BRAND_COLORS.forestGreen.main,
              color: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                borderColor: BRAND_COLORS.forestGreen.main,
                backgroundColor: `${BRAND_COLORS.forestGreen.main}10`,
              },
            }}
          >
            {t('invitation.goToLogin')}
          </Button>
        </Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url(/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* Language Switcher - top right */}
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <LanguageSwitcher variant="compact" />
        </Box>

        <Paper
          elevation={24}
          sx={{
            p: 4,
            maxWidth: 500,
            width: '100%',
            mx: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: BRAND_COLORS.forestGreen.main, mb: 1 }}
            >
              {t('invitation.successTitle')}
            </Typography>
          </Box>

          <Alert severity="success" sx={{ mb: 2 }}>
            {t('invitation.welcomeMessage', {
              organization: invitationData?.organization_name || '',
            })}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            {t('invitation.redirectingToLogin')}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} sx={{ color: BRAND_COLORS.forestGreen.main }} />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
      }}
    >
      {/* Language Switcher - top right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageSwitcher variant="compact" />
      </Box>

      <Paper
        elevation={24}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          mx: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <XERLogo width={120} />
          <Typography
            variant="h5"
            sx={{ mt: 2, fontWeight: 600, color: BRAND_COLORS.forestGreen.main }}
          >
            {t('invitation.acceptTitle')}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('invitation.invitedTo', {
              organization: invitationData?.organization_name || '',
            })}
          </Typography>
          <Typography variant="body2">
            {t('auth.email')}: {invitationData?.email || ''}
          </Typography>
          {invitationData?.role && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip label={invitationData.role} size="small" color="primary" />
            </Box>
          )}
        </Alert>

        <form onSubmit={handleAcceptInvitation}>
          <TextField
            fullWidth
            label={`${t('auth.fullName')} *`}
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            margin="normal"
            disabled={submitting}
            placeholder={t('invitation.enterFullName')}
          />

          <TextField
            fullWidth
            label={`${t('auth.password')} *`}
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            margin="normal"
            disabled={submitting}
            helperText={t('passwordReset.passwordHelperText')}
          />

          <TextField
            fullWidth
            label={`${t('auth.confirmPassword')} *`}
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            margin="normal"
            disabled={submitting}
          />

          {formErrors.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formErrors.map((err, idx) => (
                <Typography key={idx} variant="body2">
                  {err}
                </Typography>
              ))}
            </Alert>
          )}

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              mt: 3,
              py: 1.5,
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                backgroundColor: BRAND_COLORS.forestGreen.main,
                opacity: 0.8,
              },
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />{' '}
                {t('invitation.creatingAccount')}
              </>
            ) : (
              t('invitation.createAccountButton')
            )}
          </Button>
        </form>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          {t('invitation.alreadyHaveAccount')}{' '}
          <Button
            variant="text"
            onClick={() => navigate('/')}
            sx={{
              color: BRAND_COLORS.forestGreen.main,
              textTransform: 'none',
              p: 0,
              minWidth: 'auto',
            }}
          >
            {t('invitation.signInHere')}
          </Button>
        </Typography>
      </Paper>
    </Box>
  );
};
